import { Button, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import { Translate, withLocalize, LocalizeContextProps } from "react-localize-redux"
import styled from "styled-components"
import { ButtonAdd, IconGenerator, IconPatient } from "../../../components/general/mini_components"
import { DepartmentType, FUNCTIONALITY, IUnit, PatientStatus, PersonalData } from "../../../constants/types"
import {CATEGORY_DEPARTMENT_NURSE, CATEGORY_DEPARTMENT_PRESCRIPTIONS, CATEGORY_DEPARTMENT_SHOE, CATEGORY_DEPARTMENT_SOCIAL, IMG_SURVEYS, TYPE_PRESCRIPTIONS, PATIENT_TOOLBAR_SECTION_IMAGE, PATIENT_TOOLBAR_SECTION_LAB, PATIENT_TOOLBAR_SECTION_MEDICAL, PATIENT_TOOLBAR_SECTION_NURSE, PATIENT_TOOLBAR_SECTION_PRESCRIPTIONS, PATIENT_TOOLBAR_SECTION_SHOE, PATIENT_TOOLBAR_SECTION_SOCIAL, TYPE_FILL_LAB_SURVEY, TYPE_IMAGE_SURVEY, TYPE_LAB_SURVEY, TYPE_MEDICAL_SURVEY, TYPE_NURSE, TYPE_SHOE_SURVEY, TYPE_SOCIAL_SURVEY, CATEGORY_DEPARTMENT_NURSE_FW, CATEGORY_DEPARTMENT_PRESCRIPTIONS_FW, TYPE_CARE_GIVER, PATIENT_TOOLBAR_SECTION_PRESCRIPTIONS_INPATIENT, TYPE_PRESCRIPTIONS_INPATIENT } from '../../../constants';
import iconNotes from "../../../img/icons/history.png";
import iconImages from "../../../img/icons/images.png";
import iconLab from "../../../img/icons/lab.png";
import iconSocial from "../../../img/icons/social_black.png";
import iconSocialGreen from "../../../img/icons/social_green.png";
import iconShoe from "../../../img/icons/shoe.png";
import iconNurse from "../../../img/icons/nursing_black.png";
import iconNurseGreen from "../../../img/icons/nursing_green.png";
import iconNotesGreen from "../../../img/icons/history_green.png";
import iconImagesGreen from "../../../img/icons/images_green.png";
import iconLabGreen from "../../../img/icons/lab_green.png";
import iconPrescriptions from "../../../img/icons/prescription_black.svg";
import iconPrescriptionsGreen from "../../../img/icons/prescription_green.svg";
import iconPrescriptionsInpatient from "../../../img/icons/inpatients_black.png";
import iconPrescriptionsInpatientGreen from "../../../img/icons/inpatients_green.png";
import React from "react";
import { PERMISSION } from "../../../components/investigation/share/user_roles"
import { ReasonModal, useReasonPrompt } from "./ReasonPromptModal"

// Mirrors PatientDeletionErrorCode.REASON_REQUIRED in the API
// (sherwood-api/src/types/interfaces.ts). The API sends the code plus
// meta.minLength so the minimum itself is never duplicated here.
const PATIENT_DELETION_REASON_REQUIRED = 1;
// Mirrors ResearcherController.MIN_DELETE_REASON_LENGTH. Duplicated deliberately: the
// alternative is letting the server reject after a round-trip, which reads to the user
// as the action silently doing nothing. The API remains the enforcing side — this only
// moves the feedback earlier.
export const MIN_REASON_LENGTH = 10;

type SurveyType = | typeof TYPE_MEDICAL_SURVEY | typeof TYPE_IMAGE_SURVEY | typeof TYPE_SOCIAL_SURVEY | typeof TYPE_LAB_SURVEY |  typeof TYPE_SHOE_SURVEY | typeof TYPE_NURSE | typeof TYPE_CARE_GIVER | typeof TYPE_PRESCRIPTIONS | typeof TYPE_PRESCRIPTIONS_INPATIENT;

interface Props{
    personalData:PersonalData,
    patientID:number,
    years:number,
    permissions:PERMISSION[],
    functionalities:FUNCTIONALITY[],
    readMedicalPermission:boolean,
    writeMedicalPermission:boolean,
    disabled:boolean,
    enableAddButton:boolean,
    unitsResearcher:IUnit[],
    categorySurveys:number[],
    buttonSelected:number,
    patientStatus?:PatientStatus,
    buttonClickedCallBack: (typeButtonClicked: SurveyType) => void,
    editCallBack:() => void,
    addRecordCallBack: () => void,
    hospitalize?:() => void,
    prescriptionsCallBack:() => void,
    deletePatientCallBack:(reason: string) => Promise<any>,
    changeStatusCallBack?:() => void,
    showScheduleAppointment?: boolean,
    scheduleAppointmentCallBack?: () => void,
    showAddToSurgicalList?: boolean,
    addToSurgicalListCallBack?: () => void,
    showBillingShortcut?: boolean,
    billingShortcutCallBack?: () => void,
}

interface PropsComponent extends LocalizeContextProps {
    sex:string,
    patientID:number,
    healthId:string | null,
    mrnId:string | null,
    name:string,
    surnames:string,
    years:number,
    buttonSelected:number,
    buttonsAvailable:number[],
    disabledAddButton:boolean,
    readMedicalPermission:boolean,
    writeMedicalPermission:boolean,
    canDeletePatient:boolean,
    patientStatus:PatientStatus,
    buttonClickedCallBack: (typeButtonClicked: SurveyType) => void,
    editCallBack:(() => void) | null,
    addRecordCallBack: () => void,
    hospitalize?:() => void,
    deletePatientCallBack:(reason: string) => Promise<any>,
    importSourceId?: string
    changeStatusCallBack?:() => void,
    showScheduleAppointment?: boolean,
    scheduleAppointmentCallBack?: () => void,
    showAddToSurgicalList?: boolean,
    addToSurgicalListCallBack?: () => void,
    showBillingShortcut?: boolean,
    billingShortcutCallBack?: () => void,
}

const Container = styled(Grid)`
    background-color:white;
    position:sticky;
    top:53px;
    z-index:1000;
    @media (min-width: 768px) {
        top:60px;
    }
`

const DeceasedBanner = styled(Grid)`
    background-color: #ffebee;
    border: 1px solid #ef5350;
    padding: 0.5rem 1rem;
    text-align: center;
`

export const PatientToolBar:React.FC<Props> = ({personalData, patientID, disabledAddButton, permissions, functionalities,
                                                unitsResearcher, categorySurveys, buttonSelected: categorySelected, years,
                                                addRecordCallBack, hospitalize, buttonClickedCallBack, deletePatientCallBack,
                                                editCallBack, patientStatus, changeStatusCallBack, showScheduleAppointment, scheduleAppointmentCallBack,
                                                addToSurgicalListCallBack, billingShortcutCallBack}) =>{

        const isResearcherSocial = React.useMemo(() => {
            return unitsResearcher.some(unit => unit.department.type === DepartmentType.SOCIAL)
        }, [unitsResearcher])
        // same but for shoe
        const isResearcherShoe = React.useMemo(() => {
            return unitsResearcher.some(unit => unit.department.type === DepartmentType.SHOE)
        }, [unitsResearcher])
        // SHER-461: the "Add to surgical list" shortcut is gated on the
        // ADD_TO_SURGICAL_LIST permission (derived onto every surgery role and
        // enforced by the backend). A department-based visibility check was
        // considered but dropped in favour of the permission — see the ticket.
        // It is ALSO gated on the SURGERY module being enabled for the current
        // tenant (same flag that gates the /surgery/list route): tenants without
        // a surgical waiting list (e.g. Stepping Stone Hospice) still carry the
        // derived permission on their roles but must not see the icon.
        const canAddToSurgicalList = permissions.includes(PERMISSION.ADD_TO_SURGICAL_LIST)
            && functionalities.includes(FUNCTIONALITY.SURGERY)
        const canViewBilling = permissions.includes(PERMISSION.VIEW_BILLING)
            && functionalities.includes(FUNCTIONALITY.BILLING)

        if(categorySurveys.includes(CATEGORY_DEPARTMENT_SOCIAL) && !isResearcherSocial){
            categorySurveys = categorySurveys.filter(category => category !== CATEGORY_DEPARTMENT_SOCIAL)
        }
        if(categorySurveys.includes(CATEGORY_DEPARTMENT_SHOE) && !isResearcherShoe){
            categorySurveys = categorySurveys.filter(category => category !== CATEGORY_DEPARTMENT_SHOE)
        }
        if(categorySurveys.includes(CATEGORY_DEPARTMENT_PRESCRIPTIONS_FW) && !permissions.includes(PERMISSION.NURSE_FW)){
            categorySurveys = categorySurveys.filter(category => category !== CATEGORY_DEPARTMENT_PRESCRIPTIONS_FW)
        }
        const readMedicalPermission = permissions.includes(PERMISSION.MEDICAL_READ);
        const writeMedicalPermission = permissions.includes(PERMISSION.MEDICAL_WRITE);
        const canDeletePatient = permissions.includes(PERMISSION.DELETE_PATIENT);
        const canDischarge = permissions.includes(PERMISSION.DISCHARGE_PATIENT);
        const canManageStatus = permissions.includes(PERMISSION.MANAGE_PATIENT_STATUS);
        const canEditPersonalData = permissions.includes(PERMISSION.PERSONAL_ACCESS) ? editCallBack : null
        const isDeceased = patientStatus === PatientStatus.DECEASED;

        const importSourceIdRaw = (personalData as Record<string, unknown>)?.import_source_id
        const importSourceId = (typeof importSourceIdRaw === "string" || typeof importSourceIdRaw === "number")
            ? String(importSourceIdRaw).trim()
            : ""

        return <PatientToolBarComponent sex={personalData.sex} name={personalData!.name as string}
                    mrnId={personalData.automated_health_id ? personalData.automated_health_id : null}
                    healthId={personalData.health_id ? personalData.health_id : null}
                    surnames={personalData!.surnames as string} patientID={patientID}
                    buttonsAvailable={categorySurveys} buttonSelected={categorySelected}
                    readMedicalPermission={readMedicalPermission}
                    canDeletePatient={canDeletePatient}
                    deletePatientCallBack={deletePatientCallBack}
                    writeMedicalPermission={writeMedicalPermission}
                    patientStatus={patientStatus ?? PatientStatus.ACTIVE}
                    years={years} disabledAddButton={isDeceased || disabledAddButton}
                    addRecordCallBack={addRecordCallBack}
                    hospitalize={!isDeceased && hospitalize && canDischarge ? hospitalize : undefined}
                    buttonClickedCallBack={(typeButton:SurveyType) => buttonClickedCallBack(typeButton)}
                    editCallBack={canEditPersonalData}
                    importSourceId={importSourceId || undefined}
                    changeStatusCallBack={canManageStatus ? changeStatusCallBack : undefined}
                    showScheduleAppointment={!isDeceased && showScheduleAppointment}
                    scheduleAppointmentCallBack={scheduleAppointmentCallBack}
                    showAddToSurgicalList={!isDeceased && canAddToSurgicalList && Boolean(addToSurgicalListCallBack)}
                    addToSurgicalListCallBack={addToSurgicalListCallBack}
                    showBillingShortcut={!isDeceased && canViewBilling && Boolean(billingShortcutCallBack)}
                    billingShortcutCallBack={billingShortcutCallBack}
                />

}

const PatientToolBarComponentBase: React.FC<PropsComponent> = ({sex, patientID, name, surnames, readMedicalPermission,
                                                writeMedicalPermission, buttonsAvailable, healthId, mrnId,
                                                canDeletePatient, patientStatus,
                                                buttonSelected, years, disabledAddButton, buttonClickedCallBack,
                                                addRecordCallBack, hospitalize, editCallBack, deletePatientCallBack,
                                                importSourceId,
                                                changeStatusCallBack,
                                                showScheduleAppointment,
                                                scheduleAppointmentCallBack,
                                                showAddToSurgicalList,
                                                addToSurgicalListCallBack,
                                                showBillingShortcut,
                                                billingShortcutCallBack,
                                                translate,
                                            }) =>{
    // SHER-724: deleting a patient now captures a mandatory reason (the audit trail).
    // Reuses the shared prompt built for SHER-537/570 rather than the plain confirm
    // dialog this used to be. The prompt is row-indexed by design; a patient-level
    // action has a single subject, so index 0 is just "the dialog is open".
    const deletePrompt = useReasonPrompt();
    const showPrescriptions = [CATEGORY_DEPARTMENT_PRESCRIPTIONS].filter(value => buttonsAvailable.includes(value)).length > 0;
    const showPrescriptionsInpatient = [CATEGORY_DEPARTMENT_PRESCRIPTIONS_FW].filter(value => buttonsAvailable.includes(value)).length > 0;
    const showNurse = [CATEGORY_DEPARTMENT_NURSE_FW, CATEGORY_DEPARTMENT_NURSE].filter(value => buttonsAvailable.includes(value)).length > 0;
    const isDeceased = patientStatus === PatientStatus.DECEASED;

    function deletePatientModal(){
        deletePrompt.open(0);
    }
    function confirmDeletePatient(){
        deletePrompt.submit({
            requiredKey: 'hospital.patient.delete.reason_required',
            minLength: MIN_REASON_LENGTH,
            tooShortKey: 'hospital.patient.delete.reason_too_short_client',
            action: (reason) => deletePatientCallBack(reason),
            // The API owns the minimum length and sends it back as meta.minLength, so the
            // number still lives in exactly one place — this interpolates it rather than
            // keeping a second copy that could drift. Resolved to a string here rather
            // than returned as a key, because ReasonModal renders `<Translate id={...}/>`
            // with no `data` prop and would print the placeholder literally.
            resolveError: (error) => {
                const data = error?.response?.data;
                if (data?.errorCode === PATIENT_DELETION_REASON_REQUIRED) {
                    return translate('hospital.patient.delete.reason_too_short', { minLength: data?.meta?.minLength }) as string;
                }
                return 'hospital.patient.delete.error';
            },
        });
    }
    return (
        <Container item container className="patient_toolbar" xs={12}>
            <ReasonModal
                prompt={deletePrompt}
                title={<Translate id="hospital.patient.delete.title" />}
                body={<Translate id="hospital.patient.delete.description" />}
                reasonLabel={<Translate id="hospital.patient.delete.reason_label" />}
                confirmLabel="general.delete"
                onConfirm={confirmDeletePatient}
            />
            {
                isDeceased &&
                <DeceasedBanner item xs={12} data-testid="deceased-banner">
                    <Typography variant="body2" style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        <Translate id="patient.status.record-locked" />
                    </Typography>
                </DeceasedBanner>
            }
            <Grid item container xs={3} >
                {
                    editCallBack &&
                    <Grid item xs={12} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}
                        >
                        <IconPatient gender={sex ? sex : "undefined"} onClick={editCallBack} data-testid="edit-personal-details"  />
                        {
                            importSourceId &&
                            <Tooltip title="Open in interface.chom">
                                <IconButton
                                    style={{height: '40px'}}
                                    data-testid="import-source-link"
                                    onClick={() => window.open(`https://interface.chom/patient/${importSourceId}`, '_blank', 'noopener,noreferrer')}
                                    aria-label="Open patient in interface.chom"
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            canDeletePatient &&
                            <IconButton style={{height: '40px'}} data-testid="delete-patient" onClick={() => deletePatientModal()}>
                                <IconGenerator type="delete" size="small" />
                            </IconButton>
                        }
                    </Grid>
                }
            </Grid>
            <Grid item container xs={4}>
                <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                        {
                            name &&
                            name+" "+surnames
                        }
                        {
                            !name || !surnames &&
                            "Not available"
                        }
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                    {
                            mrnId &&
                            [<Translate id="investigation.create.personal_data.fields.automated_health_id" />, ": ", mrnId, <br/>]
                        }
                        {
                            healthId &&
                            [<Translate id="investigation.create.personal_data.fields.health_id" />, ": ", healthId]
                        }
                        {
                            !healthId &&
                            ["ID", ":", patientID ]
                        }

                    </Typography>
                </Grid>
                <Grid item xs={12} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <Typography variant="body2" gutterBottom>
                        {years} <Translate id="general.years" />
                    </Typography>
                    {
                        changeStatusCallBack &&
                        <Chip
                            size="small"
                            label={
                                isDeceased ? <Translate id="patient.status.deceased" /> :
                                patientStatus === PatientStatus.INACTIVE ? <Translate id="patient.status.inactive" /> :
                                patientStatus === PatientStatus.ARCHIVED ? <Translate id="patient.status.archived" /> :
                                <Translate id="patient.status.active" />
                            }
                            style={{
                                backgroundColor: isDeceased ? '#ffebee' : patientStatus === PatientStatus.INACTIVE ? '#f5f5f5' : patientStatus === PatientStatus.ACTIVE ? '#e8f5e9' : '#e0e0e0',
                                color: isDeceased ? '#d32f2f' : patientStatus === PatientStatus.ACTIVE ? '#2e7d32' : '#757575',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                            onClick={changeStatusCallBack}
                            data-testid="patient-status-chip"
                        />
                    }
                </Grid>
            </Grid>
            <Grid item container xs={5}  justifyContent="center" alignItems="center">
            {
                readMedicalPermission &&
                    <>
                        <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                            <Button data-testid="medical-notes" onClick={() => buttonClickedCallBack(TYPE_MEDICAL_SURVEY)} >
                                <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_MEDICAL ? iconNotesGreen : iconNotes} alt="Medical Notes" height="40" />
                            </Button>
                        </Grid>
                        <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                            <Button data-testid="images" onClick={() => buttonClickedCallBack(TYPE_IMAGE_SURVEY)} >
                                <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_IMAGE  ? iconImagesGreen : iconImages} alt="Images" height="40" />
                            </Button>
                        </Grid>
                        <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                            <Button data-testid="lab" onClick={() => buttonClickedCallBack(TYPE_LAB_SURVEY)} >
                                <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_LAB  ? iconLabGreen : iconLab} alt="Lab" height="40" />
                            </Button>
                        </Grid>
                    </>
            }
                {
                    buttonsAvailable.includes(CATEGORY_DEPARTMENT_SOCIAL) &&
                    <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                        <Button data-testid="social" onClick={() => buttonClickedCallBack(TYPE_SOCIAL_SURVEY)} >
                            <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_SOCIAL ? iconSocialGreen : iconSocial} alt="Social" height="50" />
                        </Button>
                    </Grid>
                }
                {
                    buttonsAvailable.includes(CATEGORY_DEPARTMENT_SHOE) &&
                    <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                        <Button data-testid="shoe" onClick={() => buttonClickedCallBack(TYPE_SHOE_SURVEY)} >
                            <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_SHOE ? iconShoe : iconShoe} alt="Social" height="50" />
                        </Button>
                    </Grid>
                }
                {
                    showNurse &&
                    <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                        <Button data-testid="nurse_fw" onClick={() => buttonClickedCallBack(TYPE_NURSE)} >
                            <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_NURSE ? iconNurseGreen : iconNurse} alt="Nurse" height="35" />
                        </Button>
                    </Grid>
                }
                {
                    showPrescriptions &&
                    <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                        <Button data-testid="show" onClick={() => buttonClickedCallBack(TYPE_PRESCRIPTIONS)} >
                            <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_PRESCRIPTIONS ? iconPrescriptionsGreen : iconPrescriptions} alt="prescriptions" height="45" />
                        </Button>
                    </Grid>
                }
                {
                    showPrescriptionsInpatient &&
                    <Grid item xs={4} style={{display: 'flex', justifyContent: 'center', alignItems:'middle'}}>
                        <Button data-testid="show" onClick={() => buttonClickedCallBack(TYPE_PRESCRIPTIONS_INPATIENT)} >
                            <img src={buttonSelected === PATIENT_TOOLBAR_SECTION_PRESCRIPTIONS_INPATIENT ? iconPrescriptionsInpatientGreen : iconPrescriptionsInpatient} alt="prescriptions" height="45" />
                        </Button>
                    </Grid>
                }
                <Grid item xs={4} style={{display:'flex', justifyContent:'center'}}>
                    {
                        showScheduleAppointment && scheduleAppointmentCallBack &&
                        <Tooltip title={<Translate id="hospital.patient.toolbar.schedule_appointment" />}>
                            <IconButton
                                data-testid="schedule-appointment"
                                aria-label={translate('hospital.patient.toolbar.schedule_appointment')}
                                onClick={scheduleAppointmentCallBack}
                                style={{height: '40px'}}
                            >
                                <IconGenerator type="appointment" size="large" />
                            </IconButton>
                        </Tooltip>
                    }
                </Grid>
                <Grid item xs={4} style={{display:'flex', justifyContent:'center'}}>
                    {
                        showAddToSurgicalList && addToSurgicalListCallBack &&
                        <Tooltip title={<Translate id="hospital.patient.toolbar.add_to_surgical_list" />}>
                            <IconButton
                                data-testid="add-to-surgical-list"
                                aria-label={translate('hospital.patient.toolbar.add_to_surgical_list')}
                                onClick={addToSurgicalListCallBack}
                                style={{height: '40px'}}
                            >
                                <IconGenerator type="surgery" size="large" />
                            </IconButton>
                        </Tooltip>
                    }
                </Grid>
                {/* SHER-1015: the slot after the surgical list falls directly under the inpatient
                    prescription icon in CHOM's three-column header. Black at the shortcut size, like
                    the appointment and surgical-list icons: it leaves the record, so it is never a
                    selected section. IconGenerator's "billing" case is not used because it ignores
                    size and paints the theme colour, and its other callers rely on that. */}
                <Grid item xs={4} style={{display:'flex', justifyContent:'center'}}>
                    {
                        showBillingShortcut && billingShortcutCallBack &&
                        <Tooltip title={<Translate id="hospital.patient.toolbar.view_patient_bills" />}>
                            <IconButton
                                data-testid="patient-billing-shortcut"
                                aria-label={translate('hospital.patient.toolbar.view_patient_bills') as string}
                                onClick={billingShortcutCallBack}
                                style={{height: '40px'}}
                            >
                                <MonetizationOnIcon style={{color:"#000"}} fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    }
                </Grid>
                <Grid item xs={4} style={{display:'flex', justifyContent:'center'}}>
                    {
                        (hospitalize) &&
                        <Button data-testid="lab" onClick={ hospitalize} >
                            <IconGenerator type="hospital" size="large" />
                        </Button>
                    }
                </Grid>
                <Grid item xs={4} style={{display:'flex', justifyContent:'center'}}>
                    {
                        writeMedicalPermission && !isDeceased &&
                        <ButtonAdd disabled={disabledAddButton} data-testid="add-record" onClick={addRecordCallBack} />
                    }
                </Grid>
            </Grid>
        </Container>
    );
}

export const PatientToolBarComponent = withLocalize(PatientToolBarComponentBase);
